import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { sharedAPI } from '@/lib/api';
import MarkdownPreview from '@/components/MarkdownPreview';
import { Lock, Pencil } from 'lucide-react';
import '@/App.css';

export default function SharedNotePage() {
  const { token } = useParams();
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    sharedAPI.getSharedNote(token)
      .then((res) => setNote(res.data))
      .catch(() => setError('Note not found or sharing has been disabled.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="shared-note-error">
        <div className="text-center">
          <Pencil className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shared-note-page">
      <header className="border-b border-border/50 px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-accent-amber" />
            <span className="text-sm font-semibold">VaultNotes</span>
          </div>
          <span className="text-xs text-muted-foreground">Shared note (read-only)</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-semibold tracking-tight mb-6" data-testid="shared-note-title">
          {note?.title || 'Untitled'}
        </h1>
        {note?.is_encrypted ? (
          <div className="p-4 bg-secondary/30 rounded-sm border border-accent-amber/30 text-center">
            <Lock className="w-6 h-6 text-accent-amber mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">This note is encrypted. Content cannot be viewed without the owner's key.</p>
          </div>
        ) : (
          <div data-testid="shared-note-content">
            <MarkdownPreview content={note?.content || ''} />
          </div>
        )}
      </main>
    </div>
  );
}
